import { useTranslation } from 'react-i18next';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import JsonLd from '../../components/seo/JsonLd';
import { useSeo } from '../../hooks/useSeo';
import { BrandRule } from '../../components/brand/BrandRule';

interface FaqEntry {
  q: { ar: string; en: string };
  a: { ar: string; en: string };
}

// Product-accurate answers only — grounded in real site behaviour (REGA
// licensing checks, the 1–5 working-day REGA reply window, PDPL data rights).
const FAQ_ENTRIES: FaqEntry[] = [
  {
    q: { ar: 'كيف أبحث عن عقار للإيجار أو الشراء؟', en: 'How do I search for a property to rent or buy?' },
    a: {
      ar: 'من الصفحة الرئيسية اختر «إيجار» أو «شراء»، ثم اكتب اسم المدينة أو الحي واختره من القائمة. يمكنك تضييق النتائج بنوع العقار والسعر وعدد الغرف من «الفلاتر»، أو البحث مباشرة على الخريطة ورسم منطقتك بنفسك.',
      en: 'From the home page pick Rent or Buy, then type a city or district and select it from the list. Narrow results by property type, price, and rooms from Filters, or search on the map and draw your own area.',
    },
  },
  {
    q: { ar: 'هل الإعلانات موثوقة ومرخّصة؟', en: 'Are the listings verified and licensed?' },
    a: {
      ar: 'نعم. كل إعلان يخضع للتحقق من ترخيص «فال» الصادر عن الهيئة العامة للعقار قبل نشره، وتظهر شارة التوثيق على الإعلانات المرخّصة. المعلنون أفراد مرخّصون أو شركات مسجّلة بسجل تجاري.',
      en: 'Yes. Every ad is checked against a valid FAL licence from the Real Estate General Authority (REGA) before it goes live, and licensed listings carry a verification badge. Advertisers are licensed individuals or CR-registered companies.',
    },
  },
  {
    q: { ar: 'كيف أعرض عقاري على المنصة؟', en: 'How do I list my property on the platform?' },
    a: {
      ar: 'سجّل حسابًا ثم اختر «أعلن عن عقارك». إن كنت وسيطًا مرخّصًا فستحتاج رقم ترخيص فال ساري المفعول. وإن كنت مالكًا بلا وسيط فيمكنك تقديم طلب تمثيل عبر المنصة ليُدار عقارك بواسطة وسيط مرخّص.',
      en: 'Create an account, then choose “List your property”. Licensed brokers need a valid FAL licence number. Owners without a broker can submit a representation request and have the property managed by a licensed agent.',
    },
  },
  {
    q: { ar: 'كم تستغرق الاستجابة للاستفسارات والشكاوى؟', en: 'How fast are inquiries and complaints answered?' },
    a: {
      ar: 'نرد على الاستفسارات والشكاوى خلال ١ إلى ٥ أيام عمل وفق متطلبات الهيئة العامة للعقار، ويصلك إشعار تأكيد فور استلام طلبك.',
      en: 'We respond to inquiries and complaints within 1–5 working days, in line with REGA requirements. You receive an acknowledgement as soon as your request is received.',
    },
  },
  {
    q: { ar: 'هل استخدام المنصة مجاني؟', en: 'Is using the platform free?' },
    a: {
      ar: 'البحث وتصفح الإعلانات والتواصل مع المعلنين مجاني تمامًا للباحثين عن عقار. المعلنون (الوسطاء والشركات) يختارون باقة اشتراك تناسب حجم نشاطهم.',
      en: 'Searching, browsing, and contacting advertisers is completely free for property seekers. Advertisers (brokers and companies) choose a subscription plan that fits their activity.',
    },
  },
  {
    q: { ar: 'كيف أتواصل مع المعلن عن عقار؟', en: 'How do I contact a property advertiser?' },
    a: {
      ar: 'من صفحة العقار ستجد أزرار الاتصال المباشر وواتساب وبيانات الوسيط المرخّص، إضافة إلى نموذج طلب معلومات داخل المنصة.',
      en: 'On the property page you will find direct call and WhatsApp buttons, the licensed broker’s details, and an in-platform information request form.',
    },
  },
  {
    q: { ar: 'ما حقوقي بخصوص بياناتي الشخصية؟', en: 'What are my rights over my personal data?' },
    a: {
      ar: 'وفق نظام حماية البيانات الشخصية (PDPL) يمكنك من إعدادات حسابك طلب نسخة من بياناتك أو حذف حسابك نهائيًا، والتحكم في موافقاتك. راجع سياسة الخصوصية للتفاصيل.',
      en: 'Under the Saudi Personal Data Protection Law (PDPL) you can export a copy of your data, permanently delete your account, and manage your consents from account settings. See the Privacy Policy for details.',
    },
  },
];

export default function FaqPage() {
  const { i18n } = useTranslation();
  const isRTL = i18n.dir() === 'rtl';
  const lang: 'ar' | 'en' = i18n.language?.startsWith('ar') ? 'ar' : 'en';

  useSeo({
    title: isRTL ? 'الأسئلة الشائعة | صفقات 24' : 'FAQ | Safaqat 24',
    description: isRTL
      ? 'إجابات عن الأسئلة الشائعة حول البحث عن العقارات، توثيق الإعلانات، عرض عقارك، وحقوق بياناتك على منصة صفقات 24.'
      : 'Answers to common questions about searching properties, listing verification, advertising your property, and your data rights on Safaqat 24.',
  });

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQ_ENTRIES.map((entry) => ({
      '@type': 'Question',
      name: entry.q[lang],
      acceptedAnswer: { '@type': 'Answer', text: entry.a[lang] },
    })),
  };

  return (
    <div className="min-h-screen bg-cream-soft text-ink" dir={i18n.dir()}>
      <Navigation />
      <JsonLd data={faqSchema} />
      <main id="main-content" className="mx-auto w-full max-w-3xl px-4 py-12 sm:px-6 sm:py-16">
        <h1 className="font-display text-3xl font-bold text-[var(--pv-color-primary)] sm:text-4xl">
          {isRTL ? 'الأسئلة الشائعة' : 'Frequently asked questions'}
        </h1>
        <BrandRule className="mt-3" />
        <p className="mt-3 max-w-xl text-[15px] text-ink-muted">
          {isRTL
            ? 'لم تجد إجابتك؟ تواصل معنا من صفحة اتصل بنا، ونرد خلال ١ إلى ٥ أيام عمل.'
            : 'Can’t find your answer? Reach us via the Contact page. We reply within 1 to 5 working days.'}
        </p>

        <div className="mt-8 space-y-3">
          {FAQ_ENTRIES.map((entry, index) => (
            <details
              key={index}
              className="group rounded-2xl border border-[var(--pv-border-soft)] bg-white px-5 py-4 open:shadow-[0_12px_32px_-24px_rgba(0,77,64,0.25)]"
            >
              <summary className="flex cursor-pointer list-none items-center justify-between gap-3 text-[15px] font-bold text-[var(--pv-color-text)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#004D40]/20 rounded [&::-webkit-details-marker]:hidden">
                {entry.q[lang]}
                <i className="ri-arrow-down-s-line shrink-0 text-lg text-[#004D40] transition-transform group-open:rotate-180" aria-hidden="true"></i>
              </summary>
              <p className="mt-3 text-[14.5px] leading-relaxed text-[var(--pv-color-text-muted)]">{entry.a[lang]}</p>
            </details>
          ))}
        </div>
      </main>
      <Footer />
    </div>
  );
}
